import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaBell,
  FaLock,
  FaCog,
  FaTicketAlt,
  FaSignOutAlt,
  FaChevronLeft,
  FaChevronRight,
  FaBars,
  FaBook,
  FaPlusCircle,
  FaChartLine
} from "react-icons/fa";
import { BiSolidPurchaseTag } from "react-icons/bi";
import { HiOutlineExternalLink } from "react-icons/hi";
import Navbar from "../../Layout/Navbar/Navbar";
import Footer from "../../Layout/Footer/Footer";
import Profile from "./ProfileComponents/ProfileComponent";
import Updates from "./Updates";
import PrivacyPolicy from "./PrivacyPolicy";
import Settings from "./Settings";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import ManageEvents from "./TicketComponents/ManageEvents";
import LogoutConfirmationModal from "./LogoutConfirmationModal";
import PurchaseLogs from "./PurchaseLogs/PurchaseLogs";
import { toast } from 'react-toastify';

// Imports for fetching and selecting profile data
import { useDispatch, useSelector } from "react-redux";
import { getUserProfile } from "../../../redux/auth/action/userProfileActions";
import { fetchAllTickets } from "../../../redux/tickets/ticketActions";
import { fetchOrganiserEvents } from "../../../redux/OrganiserEvent/organiserEventAction/GetOrganiserEventAction";

const Dashboard = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  
  // Initialize with URL param if available, otherwise use location state or default to profile
  const [selectedSection, setSelectedSection] = useState(
    sectionParam || location.state?.selectedSection || "profile"
  );
  
  // Add this useEffect to handle URL parameter changes
  useEffect(() => {
    if (sectionParam) {
      setSelectedSection(sectionParam);
    } else if (location.state?.selectedSection) {
      setSelectedSection(location.state.selectedSection);
    }
  }, [sectionParam, location.state]);

  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get profile data from Redux store
  const profileState = useSelector((state) => state.profile);
  
  useEffect(() => {
    if (!profileState.data) {
      navigate("/login", { replace: true });
    }
  }, [profileState.data, navigate]);
  
  useEffect(() => {
    dispatch(getUserProfile());
    dispatch(fetchAllTickets());
    
    // Load organizer events when profile is available
    if (profileState?.profile?.id) {
      dispatch(fetchOrganiserEvents(profileState.profile.id));
    }
  }, [dispatch, profileState?.profile?.id]);

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768;
      setIsMobile(newIsMobile);
      if (!newIsMobile) {
        setShowMobileSidebar(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const renderContent = () => {
    switch (selectedSection) {
      case "profile": return <Profile />;
      case "manage-events": return <ManageEvents />;
      case "purchase-logs": return <PurchaseLogs />;
      case "updates": return <Updates />;
      case "userend":
        window.open("https://eventfullsunday.co.uk", "_blank");
        return <Profile />; 
      case "privacy-policy": return <PrivacyPolicy />;
      case "settings": return <Settings />;
      default: return <Profile />;
    }
  };

  const handleMenuClick = (section) => {
    if (section === "create-event") {
      if (profileState?.profile?.company_name === "") {
        toast.error('You need to update your profile with your business information to create events.');
      } else {
        navigate("/createEventForm");
      }
      return;
    }
    
    if (section === "guide") {
      window.open("https://events.eventfullsunday.co.uk/", "_blank");
      return;
    }
    
    if (section === "userend") {
      window.open("https://eventfullsunday.co.uk", "_blank");
      return;
    }
    
    setSelectedSection(section);
    
    // Update URL without page reload
    const url = new URL(window.location);
    url.searchParams.set('section', section);
    window.history.pushState({}, '', url);
    
    if (isMobile) {
      setShowMobileSidebar(false);
    }
  };

  const handleLogoutClick = () => {
    setIsModalOpen(true);
  };

  const handleLogout = () => {
    // Get the refresh token
    const refreshToken = localStorage.getItem("refresh") || sessionStorage.getItem("refresh");
    
    // Close the modal
    setIsModalOpen(false);
    
    // Call the logout API endpoint
    fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000'}/logout/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    })
    .then(response => {
      console.log("Logout API response:", response.status);
      
      // Clear tokens regardless of API response
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      sessionStorage.removeItem("access");
      sessionStorage.removeItem("refresh");
      
      // Force navigation to login
      window.location.href = '/login';
    })
    .catch(error => {
      console.error("Logout API error:", error);
      
      // Clear tokens anyway even if API fails
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      sessionStorage.removeItem("access");
      sessionStorage.removeItem("refresh");
      
      // Force navigation to login
      window.location.href = '/login';
    });
  };

  // Menu items array
  const menuItems = [
    { id: "profile", icon: <FaUser />, label: "Profile" },
    { id: "create-event", icon: <FaPlusCircle />, label: "Create Event" },
    { id: "manage-events", icon: <FaTicketAlt />, label: "Manage Events" },
    { id: "purchase-logs", icon: <BiSolidPurchaseTag />, label: "Order Management" },
    { id: "updates", icon: <FaBell />, label: "Updates" },
    { id: "guide", icon: <FaBook />, label: "Organizer Guide" },
    { 
      id: "userend", 
      icon: <HiOutlineExternalLink />, 
      label: "User Portal",
      imgSrc: "https://eventfullsunday.co.uk/static/media/logo.8a47b858b01294652a4509ac13fc5b09.svg",
      imgAlt: "User Portal"
    },
    { id: "privacy-policy", icon: <FaLock />, label: "Privacy Policy" },
    { id: "settings", icon: <FaCog />, label: "Settings" },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />

      <div className="flex flex-grow relative pt-2">
        {/* Mobile sidebar overlay */}
        {showMobileSidebar && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 transition-opacity duration-300"
            onClick={() => setShowMobileSidebar(false)}
          />
        )}

        {/* Sidebar */}
        <aside 
          className={`
            ${isMobile 
              ? `fixed top-0 left-0 h-full z-40 ${showMobileSidebar ? 'translate-x-0' : '-translate-x-full'}`
              : `sticky top-[73px] h-[calc(100vh-73px)] ${isSidebarCollapsed ? 'w-20' : 'w-72'}`
            }
            bg-white shadow-lg flex-shrink-0 transition-all duration-300 ease-in-out transform
          `}
        >
          <div className="flex flex-col h-full">
            {/* Sidebar header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              {(!isSidebarCollapsed || isMobile) && (
                <h2 className="text-lg font-semibold text-[#004B95]">Dashboard</h2>
              )}
              {!isMobile && (
                <button 
                  onClick={() => setSidebarCollapsed(!isSidebarCollapsed)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
                  aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                  {isSidebarCollapsed ? <FaChevronRight size={16} /> : <FaChevronLeft size={16} />}
                </button>
              )}
              {isMobile && (
                <button 
                  onClick={() => setShowMobileSidebar(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
                >
                  <FaChevronLeft size={16} />
                </button>
              )}
            </div>

            {/* Menu items */}
            <nav className="flex-grow py-6 px-3 overflow-y-auto">
              <ul className="space-y-2">
                {menuItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => handleMenuClick(item.id)}
                      className={`
                        flex items-center w-full p-3 rounded-lg transition duration-200
                        ${selectedSection === item.id 
                          ? "bg-[#004B95] text-white shadow-md" 
                          : "text-gray-700 hover:bg-gray-100"
                        }
                        ${isSidebarCollapsed && !isMobile ? "justify-center" : "justify-start"}
                      `}
                    >
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                        {item.imgSrc ? (
                          <img 
                            src={item.imgSrc} 
                            alt={item.imgAlt || item.label} 
                            className="w-6 h-6"
                          />
                        ) : (
                          item.icon
                        )}
                      </span>
                      {(!isSidebarCollapsed || isMobile) && (
                        <span className="ml-3 text-sm font-medium">{item.label}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Logout button */}
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={handleLogoutClick}
                className={`
                  flex items-center w-full p-3 rounded-lg transition duration-200
                  text-red-500 hover:bg-red-50
                  ${isSidebarCollapsed && !isMobile ? "justify-center" : "justify-start"}
                `}
              >
                <FaSignOutAlt className="flex-shrink-0 w-6 h-6" />
                {(!isSidebarCollapsed || isMobile) && (
                  <span className="ml-3 text-sm font-medium">Logout</span>
                )}
              </button>
              
              {(!isSidebarCollapsed || isMobile) && (
                <div className="mt-6 text-center">
                  <p className="text-xs text-gray-500">
                    &copy; 2024-2025 eventfullsundays.co.uk
                  </p>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className={`flex-grow p-4 md:p-6 ${!isMobile && !isSidebarCollapsed ? 'ml-0' : ''}`}>
          {/* Breadcrumb navigation - now as a card */}
          <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center text-sm">
              <span className="text-[#004B95] font-medium">Dashboard</span>
              <span className="mx-2 text-gray-500">/</span>
              <span className="font-medium text-gray-800 capitalize">
                {selectedSection === "purchase-logs" 
                  ? "Order Management" 
                  : selectedSection === "create-event"
                    ? "Create Event"
                    : selectedSection.replace(/-/g, ' ')}
              </span>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-5 min-h-[calc(100vh-200px)]">
              {renderContent()}
            </div>
          </div>
        </main>
      </div>

      <Footer />

      {/* Logout Confirmation modal */}
      <LogoutConfirmationModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
};

export default Dashboard;
